import styled from '@emotion/styled';
import {
  palette,
  radius,
  shadow,
  spacing,
  space,
  typography,
  zIndex,
  layout,
} from '../../../style-utils/theme';
import { down, up } from '../../../style-utils/media-queries';

/**
 * THE OPERATIONS CONSOLE'S SHELL AND PRIMITIVES.
 *
 * ── WHY THIS IS NOT THE /dashboard SHELL ───────────────────────────────────
 *
 * They answer different questions. `/dashboard` is a customer's own building
 * operation: comfortable spacing, big cards, a story per screen. This is an
 * internal console where the job is "find the thing, read the number, move on",
 * and it is used with a keyboard by people who look at it for eight hours.
 *
 * So the density is deliberately tighter — 13px type, 36px rows, a permanent
 * rail, no page-level animation — while every colour, radius, shadow and space
 * still comes from `style-utils/theme.ts`. Nothing here invents a hex. The
 * design system is the vocabulary; the density is the dialect.
 *
 * ── AND WHY IT REUSES THE TABLE ENGINE ─────────────────────────────────────
 *
 * `components/table/list-table.tsx` is the one table in this repo (WP-37 —
 * there used to be seven). The console renders through it rather than starting
 * an eighth, and adds only what an ops table needs on top: a sticky header, a
 * monospace column for ids, and a compact row height.
 */

/* ── the frame ─────────────────────────────────────────────────────────── */

export const AdminRoot = styled.div`
  display: flex;
  min-height: 100vh;
  /* dvh where it exists: on iOS the browser chrome makes 100vh taller than the
     visible viewport, so the last row of a table sits under the toolbar. */
  min-height: 100dvh;
  background: ${palette.porcelain};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;

  /* STACK BELOW md. The rail turns into a full-width horizontal scroller there
     (see Rail), and in a flex ROW that left <main> squeezed to ~32px beside
     it — every console screen scrolled sideways on a phone (586px of page in a
     390px viewport). Found 2026-09-10 by the first 390px pass in
     e2e-audit/admin-console.spec.ts; the suite had only ever run at 1280/768.
     NO BACKTICKS IN THIS COMMENT - styled template literal. */
  ${down('md')} {
    flex-direction: column;
  }
`;

export const Rail = styled.nav`
  width: 232px;
  flex: 0 0 232px;
  background: ${palette.ink};
  color: ${palette.porcelain};
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  z-index: ${zIndex.sticky};

  /* THE RAIL COLLAPSES RATHER THAN DISAPPEARING. A console whose navigation is
     behind a hamburger costs a tap per screen change, and this is a tool for
     moving between screens. Below md it becomes a horizontal scroller pinned to
     the top — every destination still one tap away, no menu to open. */
  ${down('md')} {
    position: sticky;
    width: 100%;
    flex: none;
    height: auto;
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

export const RailBrand = styled.div`
  padding: ${spacing.sm} ${spacing.sm} ${spacing.xs};
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  font-family: ${typography.display};
  font-weight: ${typography.weight.semibold};
  font-size: ${typography.size.base}px;
  letter-spacing: ${typography.tracking.snug};
  border-bottom: 1px solid rgba(247, 250, 249, 0.12);

  ${down('md')} {
    border-bottom: none;
    border-right: 1px solid rgba(247, 250, 249, 0.12);
    white-space: nowrap;
  }
`;

export const RailTag = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.wide};
  text-transform: uppercase;
  color: ${palette.ink};
  background: ${palette.teal};
  border-radius: ${radius.pill};
  padding: ${spacing.half} ${spacing.xs};
`;

export const RailGroup = styled.div`
  padding: ${spacing.xs} 0;

  ${down('md')} {
    display: flex;
    padding: 0;
  }
`;

export const RailGroupLabel = styled.div`
  padding: ${spacing.xs} ${spacing.sm} ${spacing.half};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.wide};
  text-transform: uppercase;
  color: ${palette.slateLight};

  ${down('md')} {
    display: none;
  }
`;

export const RailLink = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  width: 100%;
  border: 0;
  background: ${p => (p.active ? 'rgba(0,161,154,0.18)' : 'transparent')};
  color: ${p => (p.active ? palette.tealTintSoft : 'rgba(247,250,249,0.78)')};
  box-shadow: ${p => (p.active ? `inset 3px 0 0 ${palette.teal}` : 'none')};
  padding: ${space(1)} ${spacing.sm};
  font: inherit;
  font-size: ${typography.size.sm}px;
  font-weight: ${p => (p.active ? typography.weight.semibold : typography.weight.regular)};
  text-align: left;
  cursor: pointer;
  min-height: 34px;
  transition: background 120ms ease, color 120ms ease;

  &:hover {
    background: rgba(247, 250, 249, 0.08);
    color: ${palette.porcelain};
  }
  &:focus-visible {
    outline: 2px solid ${palette.teal};
    outline-offset: -2px;
  }

  ${down('md')} {
    width: auto;
    white-space: nowrap;
    box-shadow: ${p => (p.active ? `inset 0 -3px 0 ${palette.teal}` : 'none')};
    /* 44px is the touch floor the design system sets; the rail is the one
       control on this page a finger will actually use on a tablet. */
    min-height: ${layout.touchTarget};
  }
`;

export const RailCount = styled.span`
  margin-left: auto;
  font-family: ${typography.mono};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};

  ${down('md')} {
    display: none;
  }
`;

export const Main = styled.main`
  flex: 1;
  min-width: 0; /* lets a wide table scroll inside its own frame instead of
                   stretching the page — design_system.md §9 */
  display: flex;
  flex-direction: column;
`;

export const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: ${zIndex.sticky};
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${space(2)} ${spacing.md};
  /* THE DOCK CLEARANCE, the same one number every top-anchored header uses
     (theme.ts layout.bellDockReserve). The NotificationDock is position:fixed
     and mounted for every signed-in user, operators included; without this it
     sat on top of the elevation chip and the Sign out button and took their
     clicks (found by admin-console.spec.ts, 2026-09-14). */
  padding-right: ${layout.bellDockReserve};
  background: ${palette.surface};
  border-bottom: 1px solid ${palette.line};

  ${down('sm')} {
    flex-wrap: wrap;
    padding: ${spacing.xs} ${spacing.sm};
    padding-right: ${layout.bellDockReserve};
  }
`;

export const Crumbs = styled.nav`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
  min-width: 0;

  & > * {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const CrumbLink = styled.button`
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  color: ${palette.lagoon};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

export const SearchBox = styled.input`
  flex: 1;
  min-width: 180px;
  max-width: 420px;
  height: 34px;
  padding: 0 ${spacing.xs};
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  font: inherit;
  /* 16px on touch: iOS zooms the page in on focus for anything smaller, and the
     zoom does not undo itself. The rule is in design_system.md §8. */
  ${down('md')} {
    font-size: 16px;
    height: ${layout.touchTarget};
  }
  &:focus {
    outline: none;
    border-color: ${palette.teal};
    box-shadow: 0 0 0 3px ${palette.tealTint};
  }
`;

export const Content = styled.div`
  padding: ${spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};

  ${down('sm')} {
    padding: ${spacing.sm};
    gap: ${spacing.sm};
  }
`;

/* ── headings and cards ────────────────────────────────────────────────── */

export const PageTitle = styled.h1`
  margin: 0;
  font-family: ${typography.display};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
`;

export const PageSub = styled.p`
  margin: ${spacing.half} 0 0;
  color: ${palette.slate};
  font-size: ${typography.size.xs}px;
  max-width: 78ch;
`;

export const Card = styled.section`
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};
  overflow: hidden;
`;

export const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  padding: ${space(2)} ${spacing.sm};
  border-bottom: 1px solid ${palette.line};
  flex-wrap: wrap;
`;

export const CardTitle = styled.h2`
  margin: 0;
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
`;

export const CardBody = styled.div`
  padding: ${spacing.sm};
`;

/* ── the metric strip ──────────────────────────────────────────────────── */

export const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
  gap: ${spacing.sm};
`;

export const Tile = styled.div<{ tone?: 'alert' | 'warning' | 'success' }>`
  background: ${palette.surface};
  border: 1px solid ${p => (p.tone ? 'transparent' : palette.line)};
  border-left: 3px solid
    ${p =>
      p.tone === 'alert'
        ? palette.alert
        : p.tone === 'warning'
          ? palette.warning
          : p.tone === 'success'
            ? palette.success
            : palette.teal};
  border-radius: ${radius.sm};
  padding: ${space(2)};
  box-shadow: ${shadow.xs};
  min-width: 0;
`;

export const TileLabel = styled.div`
  font-size: ${typography.size.xxs}px;
  text-transform: uppercase;
  letter-spacing: ${typography.tracking.wide};
  color: ${palette.slate};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TileValue = styled.div`
  margin-top: ${spacing.half};
  font-family: ${typography.display};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.semibold};
  line-height: 1.1;
  letter-spacing: ${typography.tracking.tight};
`;

export const TileFoot = styled.div`
  margin-top: ${spacing.half};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
`;

/* ── dense table extras on top of the shared engine ────────────────────── */

export const Mono = styled.span`
  font-family: ${typography.mono};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
`;

export const Pill = styled.span<{ tone?: keyof typeof import('../../../style-utils/theme').status }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.pill};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.medium};
  white-space: nowrap;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  flex-wrap: wrap;
`;

export const Select = styled.select`
  height: 32px;
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  padding: 0 ${spacing.xs};
  font: inherit;
  font-size: ${typography.size.xs}px;
  ${down('md')} {
    font-size: 16px;
    height: ${layout.touchTarget};
  }
`;

/* ── FORM PRIMITIVES ───────────────────────────────────────────────────────
 *
 * The console had none: the one text field it owned (the "grant admin" box in
 * AdminsScreen) carried nine inline style properties, and the promotions screen
 * needs eleven fields. Nine properties x eleven fields is how a console stops
 * looking like one product.
 *
 * `Input` matches `Select` exactly — the same height, border, radius and the
 * same 16px/44px floor below `md`, which is not decoration: anything under 16px
 * makes iOS Safari zoom the page on focus (design_system.md §8), and a control
 * shorter than the touch target is a control a thumb misses.
 */
export const Input = styled.input`
  height: 32px;
  width: 100%;
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  padding: 0 ${spacing.xs};
  font: inherit;
  font-size: ${typography.size.xs}px;
  color: ${palette.ink};

  &::placeholder {
    color: ${palette.slateLight};
  }
  &:focus-visible {
    outline: 2px solid ${palette.teal};
    outline-offset: 1px;
  }
  &:disabled {
    background: ${palette.surfaceMuted};
    color: ${palette.slate};
    cursor: not-allowed;
  }
  ${down('md')} {
    font-size: 16px;
    height: ${layout.touchTarget};
  }
`;

/* A label above its control, as one block, so a form is a list of these and
   never a hand-aligned grid that drifts at the next field. */
export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const FieldLabel = styled.span`
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.slate};
`;

/* The hint under a field. Used for the things a promotion cannot say in its
   label — what "blank" means for a limit, which intervals a duration may take. */
export const FieldHint = styled.span`
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
`;

/* Fields laid out in as many columns as fit, never fewer than one. At 320px
   this is a single column with no horizontal scroll, which §8 of the design
   system requires of every screen. */
export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: ${spacing.sm};
  align-items: start;
`;

export const Btn = styled.button<{ variant?: 'primary' | 'danger' | 'ghost' }>`
  height: 32px;
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  font: inherit;
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease;

  border: 1px solid
    ${p =>
      p.variant === 'primary'
        ? palette.teal
        : p.variant === 'danger'
          ? palette.alert
          : palette.lineStrong};
  background: ${p =>
    p.variant === 'primary'
      ? palette.teal
      : p.variant === 'danger'
        ? palette.alert
        : palette.surface};
  color: ${p => (p.variant === 'primary' || p.variant === 'danger' ? '#fff' : palette.ink)};

  &:hover:not(:disabled) {
    background: ${p =>
      p.variant === 'primary'
        ? palette.lagoon
        : p.variant === 'danger'
          ? '#D8483D'
          : palette.surfaceMuted};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid ${palette.teal};
    outline-offset: 2px;
  }

  ${down('md')} {
    min-height: ${layout.touchTarget};
  }
`;

/* ── states ────────────────────────────────────────────────────────────── */

export const Empty = styled.div`
  padding: ${spacing.xl} ${spacing.sm};
  text-align: center;
  color: ${palette.slate};
  font-size: ${typography.size.xs}px;
`;

export const ErrorBox = styled.div`
  padding: ${space(2)};
  border: 1px solid ${palette.alert};
  border-left-width: 3px;
  border-radius: ${radius.sm};
  background: #fce9e7;
  color: #b23127;
  font-size: ${typography.size.xs}px;
`;

export const NoteBox = styled.div`
  padding: ${space(2)};
  border: 1px solid ${palette.line};
  border-left: 3px solid ${palette.warning};
  border-radius: ${radius.sm};
  background: #fdf1de;
  color: #9a6207;
  font-size: ${typography.size.xs}px;
`;

/* Skeleton rows. A spinner says "something is happening"; a skeleton says
   "a table is coming and it is about this big", which stops the layout
   jumping when the rows land. */
export const SkeletonRow = styled.div`
  height: 34px;
  border-radius: ${radius.xs};
  background: linear-gradient(
    90deg,
    ${palette.surfaceMuted} 25%,
    ${palette.line} 37%,
    ${palette.surfaceMuted} 63%
  );
  background-size: 400% 100%;
  animation: adminShimmer 1.3s ease-in-out infinite;

  @keyframes adminShimmer {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
  }

  /* The console is a tool, not a showcase. Respect the OS setting. */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  & + & {
    margin-top: ${spacing.half};
  }
`;


/**
 * DENSITY AND COLUMN PRIORITY, applied to the shared table engine from outside.
 *
 * The engine's own padding (16px) is right for a customer's building screen and
 * too generous for a console where the job is to scan two hundred rows. Rather
 * than fork the engine — which is how this repo ended up with seven tables
 * before WP-37 collapsed them into one — the console wraps it and overrides two
 * things: the cell padding, and the visibility of columns marked `wide`.
 *
 * `data-wide` COLUMNS ARE HIDDEN, NOT DROPPED FROM THE DOM, on narrow
 * viewports. Below `lg` the engine already reflows rows into cards and clips
 * (not hides) the header row so the th-to-td association survives; removing a
 * column from the markup instead would break the column count between header
 * and body, which is worse than a wide table.
 */
export const Dense = styled.div`
  /* THE WIDTH THAT MAKES THE SCROLLER WORK.
     The table engine is width:100%, so inside Scroller (overflow-x:auto) it
     shrank to the frame instead of overflowing it and the scroller had nothing
     to scroll. At a normal desktop width the audit trail's ORGANIZATION and IP
     headers then wrapped one character per line. A floor on the table width
     gives the overflow the scroller was already written for.

     Only from md up: below that the engine turns rows into cards, which must
     keep filling the screen. Scoped to the console rather than set on the
     shared engine, so the other tables built on it are untouched.
     NO BACKTICKS IN THIS COMMENT - styled template literal. */
  ${up('md')} {
    table {
      min-width: 880px;
    }
  }

  th,
  td {
    padding: ${space(0.5)} ${spacing.xs};
    font-size: ${typography.size.xs}px;
  }

  /* Headers stay on one line. With the width floor above there is room for
     them, and a wrapped header in a dense operations table is noise. */
  ${up('md')} {
    th {
      white-space: nowrap;
    }
  }

  tbody tr:hover {
    background: ${palette.tealTintSoft};
  }

  ${down('lg')} {
    th[data-wide='true'],
    td[data-wide='true'] {
      display: none;
    }
  }

  /* Below md the engine turns rows into cards, so a hidden cell would leave a
     gap in the card rather than a narrower table. Keep them hidden there too —
     the point of a wide column is that the value is context, not content.
     NO BACKTICKS IN THIS COMMENT - styled template literal. */
  ${down('md')} {
    th[data-wide='true'],
    td[data-wide='true'] {
      display: none;
    }
  }
`;

/** A sticky header, so a column name is still readable 200 rows down. */
export const StickyHead = styled.div`
  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: ${palette.surface};
  }
`;

/* ── detail layout ─────────────────────────────────────────────────────── */

export const Split = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: ${spacing.md};

  ${down('lg')} {
    grid-template-columns: 1fr;
  }
`;

export const DefList = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: minmax(120px, max-content) 1fr;
  gap: ${spacing.half} ${spacing.sm};
  font-size: ${typography.size.xs}px;

  dt {
    color: ${palette.slate};
  }
  dd {
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  ${down('sm')} {
    grid-template-columns: 1fr;
    gap: 0;
    dt {
      margin-top: ${spacing.xs};
    }
  }
`;

export const Scroller = styled.div`
  /* Wide tables scroll inside their own frame — they never make the PAGE
     scroll sideways. design_system.md §9. */
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

export const Bar = styled.div<{ pct: number; tone?: 'alert' | 'warning' }>`
  height: 6px;
  border-radius: ${radius.pill};
  background: ${palette.surfaceMuted};
  overflow: hidden;
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${p => Math.max(0, Math.min(100, p.pct))}%;
    background: ${p =>
      p.tone === 'alert' ? palette.alert : p.tone === 'warning' ? palette.warning : palette.teal};
  }
`;

export const Kbd = styled.kbd`
  font-family: ${typography.mono};
  font-size: ${typography.size.xxs}px;
  border: 1px solid ${palette.lineStrong};
  border-bottom-width: 2px;
  border-radius: 4px;
  padding: 0 ${spacing.half};
  background: ${palette.surface};
  color: ${palette.slate};
`;

export const ElevationChip = styled.span<{ live: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.pill};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;
  background: ${p => (p.live ? '#EAF4E6' : palette.surfaceMuted)};
  color: ${p => (p.live ? '#356E27' : palette.slate)};
  border: 1px solid ${p => (p.live ? '#4C9F38' : palette.lineStrong)};
`;

export const Spacer = styled.div`
  flex: 1;
`;

export const DesktopOnly = styled.span`
  ${down('sm')} {
    display: none;
  }
`;

/* The console's only way out. An operator is refused the customer dashboard,
   which is where every other sign-out lives, so this one must survive every
   width — and below md it is a touch target, not a 32px desktop button. */
export const SignOutBtn = styled(Btn)`
  white-space: nowrap;
  ${down('md')} {
    height: ${layout.touchTarget};
  }
`;

export const WideOnly = styled.span`
  ${up('lg')} {
    display: inline;
  }
  display: none;
`;
